import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ArrowUp, ArrowDown, Package, Warning, Clock, CheckCircle } from '@phosphor-icons/react';

export const ActionCenterPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [kpis, setKpis] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartPeriod, setChartPeriod] = useState('monthly');
  const [chartFilter, setChartFilter] = useState({ type: 'all', id: null });
  const [pharmacies, setPharmacies] = useState([]);
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [tasks, setTasks] = useState({ urgent: [], awaiting: [], operations: [] });
  const [loading, setLoading] = useState(true);
  const [showExpiryDetail, setShowExpiryDetail] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchChartData(chartPeriod);
  }, [chartPeriod, chartFilter]);

  const fetchData = async () => {
    try {
      const [kpisRes, tasksRes, phRes, ptRes, prRes] = await Promise.all([
        api.get('/action-center/kpis'),
        api.get('/action-center/tasks'),
        api.get('/pharmacies'),
        api.get('/partners').catch(() => ({ data: [] })),
        api.get('/products'),
      ]);
      setKpis(kpisRes.data);
      setTasks(tasksRes.data);
      setPharmacies(phRes.data);
      setPartners(ptRes.data);
      setProducts(prRes.data);
      setLoading(false);
    } catch (error) { console.error(error); setLoading(false); }
  };

  const fetchChartData = async (period) => {
    try {
      const params = new URLSearchParams({ period, filter_type: chartFilter.type });
      if (chartFilter.id) params.append('filter_id', chartFilter.id);
      const res = await api.get(`/action-center/sales-chart?${params}`);
      setChartData(res.data.data || []);
    } catch (error) { console.error(error); }
  };

  if (loading) {
    return <div className="p-8">{t('common.loading')}</div>;
  }

  return (
    <div className="p-4 md:p-8 space-y-8" style={{ backgroundColor: '#F8FAFC', minHeight: '100vh', fontFamily: 'IBM Plex Sans' }}>
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>
          {t('nav.actionCenter')}
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div data-testid="kpi-this-month-sales" className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-slate-600 uppercase tracking-wide">{t('dashboard.thisMontSales')}</p>
            <ArrowUp size={20} className="text-green-600" weight="bold" />
          </div>
          <p className="text-3xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>
            RM {kpis?.this_month_sales?.toFixed(2) || '0.00'}
          </p>
        </div>

        <div data-testid="kpi-receivables" className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-slate-600 uppercase tracking-wide">{t('dashboard.receivables')}</p>
            <Clock size={20} className="text-orange-600" weight="bold" />
          </div>
          <p className="text-3xl font-bold" style={{ fontFamily: 'Manrope', color: '#0F172A' }}>
            RM {kpis?.receivables_outstanding?.toFixed(2) || '0.00'}
          </p>
        </div>

        <div data-testid="kpi-payables" className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-slate-600 uppercase tracking-wide">{t('dashboard.payables')}</p>
            <ArrowDown size={20} className="text-red-600" weight="bold" />
          </div>
          <p className="text-3xl font-bold" style={{ fontFamily: 'Manrope', color: '#0F172A' }}>
            RM {kpis?.payable_to_partners?.toFixed(2) || '0.00'}
          </p>
        </div>

        <div data-testid="kpi-expiring" onClick={() => setShowExpiryDetail(true)} className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 cursor-pointer hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-slate-600 uppercase tracking-wide">Batches Expiring (≤6mo)</p>
            <Warning size={20} className="text-yellow-600" weight="bold" />
          </div>
          <p className="text-3xl font-bold" style={{ fontFamily: 'Manrope', color: '#0F172A' }}>
            {kpis?.expiring_batches ?? 0}
          </p>
          <p className="text-xs text-slate-500 mt-1">Click for breakdown</p>
        </div>
      </div>

      {showExpiryDetail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowExpiryDetail(false)}>
          <div className="bg-white rounded-xl p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>Expiring Batches — Breakdown</h3>
              <button onClick={() => setShowExpiryDetail(false)} className="text-slate-500">✕</button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-2">Product</th>
                  <th className="text-left p-2">Owner</th>
                  <th className="text-left p-2">Batch</th>
                  <th className="text-left p-2">Expiry</th>
                  <th className="text-right p-2">Qty</th>
                </tr>
              </thead>
              <tbody>
                {(kpis?.expiring_breakdown || []).map((row, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="p-2 font-medium">{row.product_name}</td>
                    <td className="p-2 text-xs">{row.owner_name}</td>
                    <td className="p-2 font-mono text-xs">{row.batch_number}</td>
                    <td className="p-2">{row.expiry}</td>
                    <td className="p-2 text-right">{row.quantity} <span className="text-xs text-slate-500">{row.unit_label}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>{t('dashboard.salesGrowth')}</h2>
          <div className="flex flex-wrap gap-2 items-center">
            <select data-testid="chart-filter-type" value={chartFilter.type} onChange={(e) => setChartFilter({ type: e.target.value, id: null })} className="text-sm border border-slate-300 rounded px-2 py-1.5">
              <option value="all">All Sales</option>
              <option value="distributor">Distributor's Products</option>
              <option value="partner">Specific Partner</option>
              <option value="product">Specific Product</option>
              <option value="pharmacy">Specific Pharmacy</option>
            </select>
            {chartFilter.type === 'partner' && (
              <select data-testid="chart-filter-partner" value={chartFilter.id || ''} onChange={(e) => setChartFilter({ ...chartFilter, id: e.target.value })} className="text-sm border border-slate-300 rounded px-2 py-1.5">
                <option value="">Choose partner</option>
                {partners.map(p => <option key={p.id} value={p.id}>{p.company_name}</option>)}
              </select>
            )}
            {chartFilter.type === 'product' && (
              <select data-testid="chart-filter-product" value={chartFilter.id || ''} onChange={(e) => setChartFilter({ ...chartFilter, id: e.target.value })} className="text-sm border border-slate-300 rounded px-2 py-1.5">
                <option value="">Choose product</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
            {chartFilter.type === 'pharmacy' && (
              <select data-testid="chart-filter-pharmacy" value={chartFilter.id || ''} onChange={(e) => setChartFilter({ ...chartFilter, id: e.target.value })} className="text-sm border border-slate-300 rounded px-2 py-1.5">
                <option value="">Choose pharmacy</option>
                {pharmacies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
            <Tabs value={chartPeriod} onValueChange={setChartPeriod}>
              <TabsList>
                <TabsTrigger value="daily" data-testid="chart-daily">{t('dashboard.daily')}</TabsTrigger>
                <TabsTrigger value="monthly" data-testid="chart-monthly">{t('dashboard.monthly')}</TabsTrigger>
                <TabsTrigger value="yearly" data-testid="chart-yearly">{t('dashboard.yearly')}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey={chartPeriod === 'daily' ? 'day' : chartPeriod === 'monthly' ? 'label' : 'year'} stroke="#64748B" />
            <YAxis stroke="#64748B" />
            <Tooltip />
            <Bar dataKey="amount" fill="#1E3A8A" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <Button data-testid="export-chart-btn" variant="outline" className="mt-4">
          {t('common.export')}
        </Button>
      </div>

      <div className="space-y-4">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Tasks</h2>
        
        {tasks.urgent.length > 0 && (
          <div data-testid="urgent-tasks" className="bg-red-50 border-l-4 border-red-600 rounded-lg p-4">
            <h3 className="text-lg font-bold text-red-900 mb-3 flex items-center gap-2">
              <Warning size={20} weight="fill" /> Urgent ({tasks.urgent.length})
            </h3>
            <div className="space-y-2">
              {tasks.urgent.map((task, idx) => (
                <div key={idx} className="bg-white rounded-lg p-3 text-sm">
                  {task.type === 'overdue_invoice' ? (
                    <>
                      <p className="font-semibold text-red-900">Overdue Invoice {task.invoice_number}</p>
                      <p className="text-slate-700">{task.pharmacy_name} owes RM {task.amount?.toFixed(2)} — due {new Date(task.due_date).toLocaleDateString()}</p>
                    </>
                  ) : task.type === 'expiring_soon' ? (
                    <>
                      <p className="font-semibold text-red-900">{task.product_name} — Batch {task.batch_number}</p>
                      <p className="text-slate-700">Expires {task.expiry} — {task.quantity} units at {task.location === 'warehouse' ? 'warehouse' : 'pharmacy'}</p>
                    </>
                  ) : (
                    <p className="font-semibold text-red-900">{task.type}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {tasks.awaiting.length > 0 && (
          <div data-testid="awaiting-tasks" className="bg-yellow-50 border-l-4 border-yellow-600 rounded-lg p-4">
            <h3 className="text-lg font-bold text-yellow-900 mb-3 flex items-center gap-2">
              <Clock size={20} weight="fill" /> Awaiting Verification ({tasks.awaiting.length})
            </h3>
            <div className="space-y-2">
              {tasks.awaiting.map((task, idx) => (
                <div key={idx} className="bg-white rounded-lg p-3 text-sm">
                  {task.type === 'verify_payment' ? (
                    <>
                      <p className="font-semibold text-yellow-900">Payment Slip Uploaded — {task.invoice_number}</p>
                      <p className="text-slate-700">{task.pharmacy_name} — RM {task.amount?.toFixed(2)} awaiting verification</p>
                    </>
                  ) : task.type === 'partner_payment' ? (
                    <>
                      <p className="font-semibold text-yellow-900">Statement {task.statement_number}</p>
                      <p className="text-slate-700">Transfer RM {task.amount?.toFixed(2)} to {task.partner_name}</p>
                    </>
                  ) : (
                    <p className="font-semibold text-yellow-900">{task.type}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {tasks.operations.length > 0 && (
          <div data-testid="operations-tasks" className="bg-blue-50 border-l-4 border-blue-600 rounded-lg p-4">
            <h3 className="text-lg font-bold text-blue-900 mb-3 flex items-center gap-2">
              <Package size={20} weight="fill" /> Operations ({tasks.operations.length})
            </h3>
            <div className="space-y-2">
              {tasks.operations.map((task, idx) => (
                <div key={idx} className="bg-white rounded-lg p-3 text-sm">
                  <p className="font-semibold text-blue-900">{task.type.replace(/_/g, ' ')}</p>
                  {task.pharmacy_name && <p className="text-slate-700">{task.pharmacy_name}</p>}
                  {task.product_name && <p className="text-slate-700">{task.product_name}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {tasks.urgent.length === 0 && tasks.awaiting.length === 0 && tasks.operations.length === 0 && (
          <div className="bg-green-50 border-l-4 border-green-600 rounded-lg p-4 text-center">
            <CheckCircle size={48} className="text-green-600 mx-auto mb-2" weight="fill" />
            <p className="text-green-900 font-semibold">All caught up! No pending tasks.</p>
          </div>
        )}
      </div>
    </div>
  );
};